import { useQuery } from '@tanstack/react-query'
import { repositories } from '@/data'
import { useDateRange } from '@/modules/shared/hooks/use-date-range'
import type { DateRange } from '@/modules/shared/types'

/** Shared query key so the route loader and the hook stay in sync. */
export function creatorsQueryKey(clientId: string, range: DateRange) {
  return ['creators', clientId, range.from.toISOString(), range.to.toISOString()] as const
}

export function useCreators(clientId: string) {
  const [range] = useDateRange()
  return useQuery({
    queryKey: creatorsQueryKey(clientId, range),
    queryFn: () => repositories.creators.getCreators(clientId, range),
  })
}

export function useCreatorById(clientId: string, creatorId: string) {
  const [range] = useDateRange()
  return useQuery({
    queryKey: [...creatorsQueryKey(clientId, range), creatorId],
    queryFn: () => repositories.creators.getCreatorById(clientId, creatorId, range),
    enabled: !!creatorId,
  })
}

export function useCreatorVideos(clientId: string, username: string) {
  const [range] = useDateRange()
  return useQuery({
    queryKey: [...creatorsQueryKey(clientId, range), 'videos', username],
    queryFn: () => repositories.creators.getCreatorVideos(clientId, username, range),
    enabled: !!username,
  })
}

export function useLiveCreators(clientId: string) {
  return useQuery({
    queryKey: ['creators', 'live', clientId],
    queryFn: () => repositories.creators.getLiveCreators(clientId),
  })
}

export function useTargetCollabs(clientId: string) {
  return useQuery({
    queryKey: ['creators', 'collabs', clientId],
    queryFn: () => repositories.creators.getTargetCollabs(clientId),
  })
}

export function useCollaborationData(clientId: string) {
  return useQuery({
    queryKey: ['creators', 'collaboration-data', clientId],
    queryFn: () => repositories.creators.getCollaborationData(clientId),
  })
}

export function useCreatorIncentives(clientId: string) {
  return useQuery({
    queryKey: ['creators', 'incentives', clientId],
    queryFn: () => repositories.creators.getCreatorIncentives(clientId),
  })
}
